import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import IconeAjuda from '@/global/components/IconeAjuda'
import { SimpleTabs } from '@/global/components/Tabs'
import Grid from '@/global/components/ui/Grid'
import { FormFieldCheckbox, FormFieldSelect } from 'global/components/Form'
import { LockIcon } from 'lucide-react'
import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

export const PontoWeb: React.FC = () => {
    const { watch, setValue } = useFormContext()
    const [registroApp, reconhecimentoFacial, dtRescisao] = watch([
        'registroApp',
        'reconhecimentoFacial',
        'dtRescisao',
    ])

    useEffect(() => {
        if (!registroApp) setValue('reconhecimentoFacial', 0)
        else if (registroApp && reconhecimentoFacial === 0)
            setValue('reconhecimentoFacial', 1)
    }, [registroApp])

    return (
        <section id="ponto-web">
            <HorizontalDivider className="mb-4 mt-2">
                Registro de ponto
            </HorizontalDivider>
            <Overlay>
                <Grid container columns={24}>
                    <Grid item xs={12}>
                        <FormFieldCheckbox
                            name="verSaldoHoras"
                            label="Permite ver o saldo de horas"
                            disabled={!!dtRescisao}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormFieldCheckbox
                            name="registroWeb"
                            label="Permite registro pela web"
                            disabled={!!dtRescisao}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormFieldCheckbox
                            name="registroApp"
                            label="Permite registro pelo app"
                            disabled={!!dtRescisao}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormFieldCheckbox
                            name="ajustesPonto"
                            label="Permite solicitar ajustes de registros"
                            disabled={!!dtRescisao}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormFieldSelect
                            label="Método de registro"
                            name="reconhecimentoFacial"
                            disabled={!registroApp || !!dtRescisao}
                            defaultValue={0}
                            options={[
                                {
                                    value: 0,
                                    label: 'Não permitido',
                                    disabled: true,
                                },
                                { value: 1, label: 'Por clique' },
                                { value: 2, label: 'Selfie' },
                                { value: 3, label: 'Reconhecimento facial' },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormFieldSelect
                            name="pontoDiferenciado"
                            label="Ponto diferenciado"
                            disabled={!!dtRescisao}
                            options={[
                                { value: 'NORMAL', label: 'Normal' },
                                { value: 'EXCECAO', label: 'Exceção' },
                                {
                                    value: 'PRE_ASSINALADO',
                                    label: 'Pré-assinalado',
                                },
                                {
                                    value: 'SEM_INTERVALO',
                                    label: 'Sem intervalo',
                                },
                            ]}
                            endAdornment={
                                <IconeAjuda borderless>
                                    <AjudaPontoDiferenciado />
                                </IconeAjuda>
                            }
                        />
                    </Grid>
                </Grid>
            </Overlay>
        </section>
    )
}

function Topico({
    titulo,
    children,
}: {
    titulo: string
    children: React.ReactNode
}) {
    return (
        <div className="mt-3 first:mt-0">
            <span className="font-semibold">{titulo}</span>
            <div className="text-sm">{children}</div>
        </div>
    )
}

function TabContainer({ children }: { children: React.ReactNode }) {
    return <div className="h-75 overflow-y-auto">{children}</div>
}

function AjudaPontoDiferenciado() {
    return (
        <SimpleTabs
            className="gap-2 w-[26rem]"
            tabs={[
                {
                    value: 'NORMAL',
                    label: 'Normal',
                    content: (
                        <TabContainer>
                            <Topico titulo="O que é">
                                Regime padrão: o funcionário registra todas as
                                marcações do dia — entrada, saída para o
                                intervalo, retorno do intervalo e saída.
                            </Topico>
                            <Topico titulo="Na prática">
                                O sistema não cria nenhuma marcação
                                automaticamente. O que faltar aparece como
                                inconsistência no espelho e precisa ser
                                resolvido na manutenção do ponto.
                            </Topico>
                        </TabContainer>
                    ),
                },
                {
                    value: 'EXCECAO',
                    label: 'Exceção',
                    content: (
                        <TabContainer>
                            <Topico titulo="O que é">
                                O funcionário não registra o ponto quando cumpre
                                a jornada normal. Só o que fugir do padrão
                                (atraso, hora extra, saída antecipada) é
                                registrado.
                            </Topico>
                            <Topico titulo="Na prática">
                                No cálculo do ponto, se o dia estiver sem
                                nenhuma marcação, o sistema gera todos os
                                horários previstos na tabela de horário do dia,
                                com origem “Ponto exceção”. Se existir qualquer
                                marcação no dia — ou afastamento, abono,
                                compensação, folga ou falta cadastrada — o
                                sistema não gera nada e o dia deve ser tratado
                                na manutenção do ponto.
                            </Topico>
                        </TabContainer>
                    ),
                },
                {
                    value: 'PRE_ASSINALADO',
                    label: 'Pré-assinalado',
                    content: (
                        <TabContainer>
                            <Topico titulo="O que é">
                                O intervalo para repouso e alimentação já vem
                                previamente registrado. O funcionário marca
                                apenas a entrada e a saída da jornada.
                            </Topico>
                            <Topico titulo="Na prática">
                                Ao bater a entrada (até 2 horas antes ou depois
                                da entrada prevista do primeiro turno), o
                                sistema já cria a saída para o intervalo. Ao
                                bater a saída (até 2 horas antes ou depois da
                                saída prevista do último turno), o sistema já
                                cria o retorno do intervalo. As marcações
                                geradas ficam com origem “Ponto pré-assinalado”.
                            </Topico>
                            <Topico titulo="Observações">
                                Marcações lançadas pelo supervisor não geram o
                                par automático. Saída registrada até as 02:00
                                gera o pré-assinalado no dia anterior. O cálculo
                                do ponto não cria marcações de intervalo: se o
                                par não foi gerado na batida, o dia precisa ser
                                tratado na manutenção do ponto.
                            </Topico>
                        </TabContainer>
                    ),
                },
                {
                    value: 'SEM_INTERVALO',
                    label: 'Sem intervalo',
                    content: (
                        <TabContainer>
                            <Topico titulo="O que é">
                                O funcionário marca só a entrada e a saída da
                                jornada; o intervalo nunca é marcado por ele.
                            </Topico>
                            <Topico titulo="Na prática">
                                No cálculo do ponto, o sistema insere as
                                marcações de intervalo previstas na tabela de
                                horário entre a primeira e a última marcação do
                                dia. As marcações geradas ficam com origem
                                “Manual Supervisor”.
                            </Topico>
                            <Topico titulo="Diferença do pré-assinalado">
                                Aqui a geração ocorre no cálculo do ponto, e não
                                no momento da batida, e exige que entrada e
                                saída estejam dentro das tolerâncias da tabela
                                de horário.
                            </Topico>
                        </TabContainer>
                    ),
                },
            ]}
        />
    )
}

function Overlay({ children }: { children: React.ReactNode }) {
    const { watch } = useFormContext()
    const bloqueado = watch('dtRescisao')

    return (
        <div className="relative flex items-center justify-center size-full">
            {!!bloqueado && (
                <div className="absolute bg-[rgba(0,0,0,0.5)] size-full flex items-center justify-center backdrop-blur-[5px] pointer-events-none z-[10] cursor-not-allowed">
                    <LockIcon className="size-20" />
                    <h5 className="text-lg font-semibold text-white ml-4 uppercase">
                        Funcionário rescindido
                    </h5>
                </div>
            )}
            {children}
        </div>
    )
}
